<template>
  <transition name="transition-scale-fade">
    <div
      v-show="noPreferencesAndGdpr || isShowingManagement"
      class="modal"
      style="--modal-zindex: var(--depth-3)"
    >
      <div
        id="cookie-consent"
        class="transition-scale-fade-content yellow position-absolute bottom-0 left-0 column-12 tablet:column-7 laptop:column-5 desktop:column-4 padding-lg"
        style="background: transparent; visibility: visible"
      >
        <div
          class="card header roomy text-left with-border margin-bottom-0"
          style="--border-color: var(--fa-dk-yellow)"
        >
          <h2 class="h4">
            <font-awesome-icon
              :icon="$v7.solid.faCookieBite"
              size="xl"
              class="margin-right-2xs"
            />
            Cookie. Cookie. Cookie.
          </h2>
          <p>
            We use cookies to make our site work better by storing limited information about your usage of the site.
            <a
              class="blue"
              href="https://github.com/FortAwesome/Security-Docs-and-Attestation/blob/main/Font_Awesome_Cookie_Info.md"
              target="_blank"
              >Read more here.</a
            >
          </p>

          <div
            v-if="isShowingCookieOptions"
            class="margin-bottom-xl display-flex flex-column"
            style="gap: var(--spacing-2xs)"
          >
            <customize-cookies-checkbox
              v-for="(option, index) in cookieOptions"
              :key="option.title"
              :description="option.description"
              :is-checked="cookieIsChecked(option)"
              :is-last="cookieOptions.length === index + 1"
              :is-required="option.isRequired"
              :is-showing-description="option.isShowingDescription"
              :title="option.title"
              @showCookieDescription="showCookieDescription"
              @change="(value) => cookiePreferenceChange(option, value)"
            />
          </div>

          <div class="row margin-y-xs">
            <div class="column">
              <button
                class="compact block flat clear with-border"
                style="--button-color: var(--fa-navy); border-color: var(--fa-navy)"
                @click="declineAll"
              >
                Necessary Only
              </button>
            </div>
            <div class="column">
              <button
                class="compact block flat clear with-border"
                style="--button-color: var(--fa-navy); border-color: var(--fa-navy)"
                @click="showSaveOption"
              >
                {{ actionText }}
              </button>
            </div>
          </div>

          <button
            class="block primary"
            style="--button-hover-background: var(--fa-dk-yellow)"
            @click="acceptAll"
          >
            <font-awesome-icon
              :icon="$v7.solid.faCookieBite"
              size="xl"
              class="padding-right-2xs"
            />
            Accept All Cookies
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import { camelCase } from 'lodash'
  import { mapGetters, mapActions } from 'vuex'
  import CustomizeCookiesCheckbox from './CustomizeCookiesCheckbox'

  export default {
    name: 'CookieConsent',

    components: {
      CustomizeCookiesCheckbox
    },

    data() {
      return {
        isShowingCookieOptions: false,
        strictlyNecessary: true,
        analytics: false,
        functionality: false,
        performance: false,
        advertising: false,
        actionText: 'Customize',
        cookieOptions: [
          {
            title: 'Strictly Necessary',
            dataKey: 'strictlyNecessary',
            description: 'These cookies are required to make the site function correctly. They cannot be turned off.',
            isRequired: true,
            isShowingDescription: false
          },
          {
            title: 'Functionality',
            dataKey: 'functionality',
            description: 'These cookies allow for additional functionality but are not strictly required for the site to function correctly.',
            isRequired: false,
            isShowingDescription: false
          },
          {
            title: 'Performance',
            dataKey: 'performance',
            description: 'These cookies allow for improved site performance.',
            isRequired: false,
            isShowingDescription: false
          },
          {
            title: 'Advertising',
            dataKey: 'advertising',
            description: 'These cookies are used for advertising purposes.',
            isRequired: false,
            isShowingDescription: false
          },
          {
            title: 'Analytics',
            dataKey: 'analytics',
            description:
              'These cookies allow us to gather data about site usage so we can better understand user preferences or improve aspects of the site, etc.',
            isRequired: false,
            isShowingDescription: false
          }
        ]
      }
    },

    computed: {
      ...mapGetters('cookiePreferences', ['preferences', 'noPreferencesAndGdpr', 'isShowingManagement'])
    },

    created() {
      this.analytics = this.preferences.analytics
      this.functionality = this.preferences.functionality
      this.performance = this.preferences.performance
      this.advertising = this.preferences.advertising
    },

    methods: {
      ...mapActions('cookiePreferences', ['setPreferences', 'hideCookiePreferences']),

      showSaveOption() {
        if (this.isShowingCookieOptions) {
          this.savePreferences()
        } else {
          this.isShowingCookieOptions = true
          this.actionText = 'Confirm Choices'
        }
      },

      showCookieDescription(title) {
        const showingSomething = camelCase(title)

        this.cookieOptions.forEach((item, index) => {
          if (camelCase(item.title) === showingSomething) {
            this.cookieOptions[index].isShowingDescription = !this.cookieOptions[index].isShowingDescription
          } else {
            this.cookieOptions[index].isShowingDescription = false
          }
        })
      },

      acceptAll() {
        const preferences = {}

        this.advertising = preferences.advertising = true
        this.analytics = preferences.analytics = true
        this.functionality = preferences.functionality = true
        this.performance = preferences.performance = true

        this.setPreferences({ preferences })
      },

      savePreferences() {
        const preferences = {}

        preferences.advertising = this.advertising
        preferences.analytics = this.analytics
        preferences.functionality = this.functionality
        preferences.performance = this.performance

        this.setPreferences({ preferences })
      },

      declineAll() {
        const preferences = {}

        this.advertising = preferences.advertising = false
        this.analytics = preferences.analytics = false
        this.functionality = preferences.functionality = false
        this.performance = preferences.performance = false

        this.setPreferences({ preferences })
      },

      cookieIsChecked(option) {
        return this[option.dataKey]
      },

      cookiePreferenceChange(option, value) {
        this[option.dataKey] = value
      }
    }
  }
</script>
